import {ChangeEventHandler, KeyboardEventHandler, useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import './Comments.less';
import CommentCard from "./CommentCard";
import Comment from "../entities/comment";
import util from "../lib/util";
import * as CommentActions from "../store/actions/commentActions";
import {confirmWarningOkCancel, confirmWarningOkCancelNative} from "../ui/Confirm";
import {deleteComment} from '../store/deps/commentDeps';
import IconButton from '../ui/IconButton/IconButton';
import Icons from '../ui/IconButton/Icons';
import locals from '../lib/localStorage';
import {InView} from 'react-intersection-observer';
import {formatLFMFromId} from "../Contacts/ContactFormat";
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";
import newHRTimestamp from "../lib/picos/timestamp";
import {TextField} from '@mui/material';
import {useRootDispatch} from "../hooks/useOutlawStore";
import {uuid} from "../@types/schema";
import Task from "../entities/task";
import {connect} from "react-redux";
import type {RootState} from "../@types/store";
import useComponentCache from "../hooks/useComponentCache";
import {LocalStorageType, useLocalStorage} from "../hooks/useLocalStorage";
import {isMacOS, isWindows} from "../lib/picos/browserFns";
import {stripHtml} from 'string-strip-html';
import Searchbar from '../Search/Searchbar';

/**
 * Compartmentalize a set of comments for an entity.
 *
 * 02/27/2020 - adding the ability to see more than just comments related to the specific named entity.
 * 11/17/2020 - making this component directly save results, rather than commit with a parent.
 *
 * For example, if the comments are related to a Lead, this component will show not only comments related to the lead,
 * but also comments related to tasks associated with the lead.
 *
 * Here is the breakdown:
 *
 * Task: show only comments related to the Task
 * Campaign: show comments related to the Campaign, or Tasks related to the Campaign
 * Lead: show comments related to the Lead, or Tasks related to the Lead
 * Case: show comments related to the Case, the Lead for the case, or Tasks related to the Case or Lead
 * Contact: show comments related to the Contact, or any Lead/Case the Contact is involved in, or Tasks for the Contact/Lead/Case.
 */

type CommentsState = {
    currentEntry: string,
    isAddDisabled: boolean
}

const reverseSort = (sortKey: SortKeys) => {
    if (sortKey === 'asc') return 'desc';
    else return 'asc';
}

const Comments = (props: CommentsProps) => {
    // Global cache (in case user pages away and comes back)
    const {cacheSave, cacheReset, cacheValue} = useComponentCache<CommentsState>(`comment-add-${props.entityId}`);

    // Global state
    const {comments, tasks, leads, matters, formatRelated = true, isEditing = true, entityType, entityId} = props;

    // State
    const [currentEntry, setCurrentEntry] = useState<string>(cacheValue('currentEntry', ''));
    const [isAddDisabled, setIsAddDisabled] = useState<boolean>(cacheValue('isAddDisabled', false));
    const [sortOrder, setSortOrder] = useLocalStorage<LocalStorageType.string, SortKeys>(locals.COMMENT_SORT_ORDER, LocalStorageType.string, 'desc');
    const [newId, ] = useState<number>(newHRTimestamp());
    const [focused, setFocused] = useState<boolean>(true);
    const [search, setSearch] = useState('');
    const searchTerms = useMemo(() => search ? search.split(' ') : [], [search]);
    const onSearch = useCallback((terms: string[]) => setSearch(terms.join(' ')), []);

    const doResetRef = useRef<boolean>(false);

    // References (remembered between renders)
    let added = useRef<Comment[]>([]);
    let autoFocus = useRef<boolean>(false);
    
    // Add a ref to track if an operation is in progress
    const operationInProgressRef = useRef<boolean>(false);

    // Dispatcher (global state)
    const dispatch = useRootDispatch();

    useLayoutEffect(() => {
        const doReset = doResetRef.current;

        return () => {
            if (doReset) cacheReset();
            else cacheSave({currentEntry, isAddDisabled});
        }
    }, [cacheReset, cacheSave, currentEntry, isAddDisabled]);

    /**
     * Filter out tasks to use for Associated Tasks
     */
    const taskFilter = useCallback((task: Task, entityId: string): boolean => {
        return task.tieIns?.some(ti => ti.id === entityId) || false;
    }, []);

    /**
     * Check if the task is related to the contact. This one is much more complex!
     */
    const contactFilter = useCallback((task: Task, contactId: string): boolean => {
        // Check if this task includes the contactId in the 'participants' field
        if (task.relatedTo.includes(contactId)) return true;

        // Check if this task is related to a lead or matter with this person as a client
        const matterIds = task.tieIns?.filter(ti => ti.module === 'Client').map(ti => ti.id) || [];

        const matterLeads = matters
            .filter(matter => matterIds.includes(matter.id))
            .map(matter => matter.leadId);

        const leadIds = task.tieIns?.filter(ti => ti.module === 'Sales').map(ti => ti.id) || [];

        const allLeadIds = matterLeads.concat(leadIds);

        return leads
            .filter(lead => allLeadIds.includes(lead.id))
            .filter(util.deletedFilter)
            .map(lead => lead.clients)
            .flat()
            .includes(contactId);
    }, [matters, leads]);

    /**
     * Get task-related comments for the given id.
     */
    const getRelatedTasks = useCallback((_comments: Comment[], id: uuid, filter: (task: Task, id: string) => boolean): Comment[] => {
        return tasks
            .filter(task => filter(task, id))
            .filter(util.deletedFilter)
            .map(task => {
                return _comments.filter(c => c.entityId === task.id)
                    .map(c => {
                        c.sourceType = 'Task';
                        c.sourceLabel = task.title;
                        return c;
                    });
            })
            .flat();
    }, [tasks]);

    const getCampaignTasks = useCallback(
        (_comments: Comment[], id: string) => getRelatedTasks(_comments, id, taskFilter),
        [getRelatedTasks, taskFilter]
    );
    const getLeadTasks = useCallback(
        (_comments: Comment[], id: string) => getRelatedTasks(_comments, id, taskFilter),
        [getRelatedTasks, taskFilter]
    );
    const getMatterTasks = useCallback(
        (_comments: Comment[], id: string) => getRelatedTasks(_comments, id, taskFilter),
        [getRelatedTasks, taskFilter]
    );
    const getContactTasks = useCallback(
        (_comments: Comment[], id: string) => getRelatedTasks(_comments, id, contactFilter),
        [getRelatedTasks, contactFilter]
    );

    /**
     * Get comments from leads or matters related to this contact.
     */
    const getLeadAndMatterComments = useCallback((contactId: uuid) => {
        const _leads = leads
            .filter(lead => lead.clients.includes(contactId))
            .filter(util.deletedFilter);

        const leadIds = _leads.map(lead => lead.id);

        const relatedLeads = _leads.map(lead => comments.filter(c => c.entityType === 'lead' && c.entityId === lead.id)
            .map(comment => {
                comment.sourceType = 'Lead';
                comment.sourceLabel = lead.name;
                return comment;
            })
            .filter(util.existsFilter))
            .flat();

        const _matters = matters
            .filter(matter => leadIds.includes(matter.leadId))
            .filter(util.existsAndNotDeletedFilter);

        const relatedMatters = _matters
            .map(matter => comments.filter(c => c.entityType === 'matter' && c.entityId === matter.id)
                .map(comment => {
                    comment.sourceType = 'Matter';
                    comment.sourceLabel = matter.name;
                    return comment;
                }))
            .flat();

        return relatedLeads.concat(relatedMatters);
    }, [leads, matters, comments]);

    /**
     * Get comments from a lead.
     */
    const getLeadComments = useCallback((leadId: uuid): Array<Comment> => {
        const lead = leads.get(leadId);

        return comments
            .filter(c => c.entityType === 'lead' && c.entityId === leadId)
            .map(comment => {
                comment.sourceType = 'Lead';
                comment.sourceLabel = lead.name;
                return comment;
            })
            .filter(util.existsFilter);
    }, [leads, comments]);

    /**
     * Get comments from contacts related to an entity.
     */
    const getContactComments = useCallback((entityType: string, entityId: string) => {
        let cids: string[] = [];
        if (entityType === 'lead') {
            cids = leads
                .filter(l => l.id === entityId)
                .filter(util.deletedFilter)
                .map(l => l.clients || [])
                .flat();
        }
        else if (entityType === 'matter') {
            cids = matters
                .filter(m => m.id === entityId)
                .filter(util.deletedFilter)
                .map(m => m.clients || [])
                .flat();
        }

        return comments
            .filter(c => c.entityType === 'contact' && cids.includes(c.entityId))
            .filter(util.deletedFilter)
            .map(comment => {
                comment.sourceType = 'Contact';
                comment.sourceLabel = formatLFMFromId(comment.entityId);
                return comment;
            });
    }, [leads, matters, comments])

    /**
     * Get all comments related to this entity.
     *
     * @return {Comment[]}
     */
    const relatedComments = useMemo(() => {
        const results: Comment[][] = [];

        const _comments = comments.filter(c => c.entityType === 'task' && c.status !== 'D');

        // If this is not a task, we need to find related stuff
        if (entityType === 'campaign') {
            // Need a list of related tasks for this campaign
            results.push(getCampaignTasks(_comments, entityId));
        }

        else if (entityType === 'lead') {
            results.push(getLeadTasks(_comments, entityId));
            results.push(getContactComments('lead', entityId));
        }

        else if (entityType === 'matter') {
            const matter = matters.get(entityId);
            results.push(getMatterTasks(_comments, entityId));
            if (matter?.leadId) results.push(getLeadComments(matter.leadId));
            results.push(getContactComments('matter', entityId));
        }

        else if (entityType === 'contact') {
            // These are comments from tasks that are related to leads or matters related to the contact
            results.push(getContactTasks(_comments, entityId));

            // Now we need comments from the cases and leads themselves
            results.push(getLeadAndMatterComments(entityId));
        }

        return results.flat();
    }, [entityType, entityId, comments, matters, getCampaignTasks, getLeadTasks, getMatterTasks, getContactTasks, getContactComments, getLeadComments, getLeadAndMatterComments]);

    /**
     * Add a new comment.
     */
    const onAdd = useCallback(() => {
        if (operationInProgressRef.current) return;
        if (!currentEntry) return;
        
        operationInProgressRef.current = true;
        
        try {
            const comment = new Comment({
                firmId: getCurrentFirmId(),
                commentId: util.genUUID(),
                addedBy: getCurrentUserId(),
                addedOn: newHRTimestamp(),
                entityType,
                entityId,
                note: currentEntry?.replaceAll('\n', '<br/>') || '',
            });

            added.current.push(comment);

            // Set remembered values
            doResetRef.current = true;

            autoFocus.current = true;
            if (!focused) setFocused(true);

            setIsAddDisabled(true);
            setCurrentEntry('');
            setSearch('');

            dispatch(CommentActions.saveCommentQueue(comment));
        } finally {
            // Reset the flag after a short delay
            setTimeout(() => {
                operationInProgressRef.current = false;
            }, 100);
        }
    }, [currentEntry, entityType, entityId, focused, dispatch]);

    /**
     * Change sort order
     */
    const onSort = useCallback(() => {
        const newValue = reverseSort(sortOrder);
        setSortOrder(newValue);

        locals.setString(locals.COMMENT_SORT_ORDER, newValue);
    }, [sortOrder, setSortOrder]);

    /**
     * A comment has been deleted.  Remove it.
     *
     * @param {Comment} comment
     */
    const onDelete = useCallback((comment: Comment) => {
        if (operationInProgressRef.current) return;
        
        const onOk = () => {
            if (operationInProgressRef.current) return;
            operationInProgressRef.current = true;
            
            try {
                deleteComment(comment);

                // If this was added in this session, remove from state
                const index = added.current.findIndex(c => c.id === comment.id);
                if (index !== -1) {
                    added.current.splice(index, 1);
                }
            } finally {
                setTimeout(() => {
                    operationInProgressRef.current = false;
                }, 100);
            }
        }

        const fn = props.embedded ? confirmWarningOkCancelNative : confirmWarningOkCancel;
        fn('Confirm Delete', 'Are you sure you want to delete this comment?', 'Yes', 'No', onOk);
    }, [props.embedded]);

    /**
     * A comment has been added.  Save it.
     *
     * @param {Comment} comment
     */
    const onEdit = useCallback((comment: Comment) => {
        if (operationInProgressRef.current) return;
        
        if (!comment.note || comment.note.replaceAll('&nbsp;', '').trim() === '') {
            onDelete(comment);
            return;
        }

        operationInProgressRef.current = true;
        
        try {
            const c = comment.clone();
            c.addedBy = getCurrentUserId();
            c.addedOn = newHRTimestamp();
            c.status = 'U';

            // If this was added in this session, remove from state
            const index = added.current.findIndex(c => c.id === comment.id);
            if (index !== -1) {
                added.current.splice(index, 1, comment);
            }

            dispatch(CommentActions.saveCommentQueue(c));
        } finally {
            setTimeout(() => {
                operationInProgressRef.current = false;
            }, 100);
        }
    }, [dispatch, onDelete]);

    /**
     * Text has been entered into the new comment field.
     *
     * @param {SyntheticEvent} e
     * @param {any} data
     * @param {string} value
     */
    const onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback((e) => {
        setIsAddDisabled(!e.target.value?.trim());
        setCurrentEntry(e.target.value || '');
    }, [setIsAddDisabled, setCurrentEntry]);

    const onKeyDown: KeyboardEventHandler = useCallback(e => {
        if (e.key === 'Enter' && e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            onAdd();
        }
    }, [onAdd]);

    /**
     * Note when focused on the field. We do not need to show warnings here.
     */
    const onFocusIn = useCallback(() => {
        if (!focused) setFocused(true);
    }, [focused]);

    /**
     * Note when focusing away from the field. We need to show warnings here.
     */
    const onFocusOut = useCallback(() => {
        if (focused) setFocused(false);
    }, [focused]);

    // Start with the comments passed in
    let _comments = comments.getMappingFor('entityId', entityId);

    // If we are looking for related tasks
    if (formatRelated) _comments = _comments.concat(relatedComments);

    const query = search.trim().toLocaleLowerCase().replace(/\s+/g, ' ');
    if (query) {
        const terms = query.split(' ');
        _comments = _comments.filter(comment => {
            const text = stripHtml(comment.note ?? '').result.toLocaleLowerCase().replace(/\s+/g, ' ');
            return terms.every(term => text.includes(term));
        });
    }

    // Sort the results
    _comments = _comments.sort((a, b) => {
        // Need to use either createdOn (date, if existing) or addedOn (number, if just added)
        const _a = a.createdOn
                   ? a.createdOn
                   : new Date(a.addedOn / 1000);
        const _b = b.createdOn
                   ? b.createdOn
                   : new Date(b.addedOn / 1000);

        return sortOrder === 'asc' ? _a.getTime() - _b.getTime() : _b.getTime() - _a.getTime();
    });

    // If we are NOT looking for related tasks, strip out any source info (it will get in there)
    if (!formatRelated) _comments = _comments.map(c => {
        c.sourceType = null;
        c.sourceLabel = null;
        return c;
    });

    const sortIcon = sortOrder === 'asc' ? Icons.TIME_SORT_ASC : Icons.TIME_SORT_DESC;

    const alertStyle = !focused && !isAddDisabled ? 'comments comments--alert' : 'comments';

    const addMessage = isMacOS()
                       ? 'Enter comment, press ⌘ + ENTER to add'
                       : isWindows()
                         ? 'Enter comment, press CTRL + ENTER to add'
                         : 'Add a comment';

    return (
        <div className={alertStyle}
             onFocus={onFocusIn}
             onBlur={onFocusOut}
        >
            {/* Comments */}
            {isEditing &&
				<div key={`comment-${entityType}-${entityId}`} className="comments__add-comment">
					<TextField id={newId.toString() || `comment-${entityType}-${entityId}`}
					           placeholder={addMessage}
                               aria-placeholder={addMessage}
					           value={currentEntry || ''}
					           onKeyDown={onKeyDown}
					           onChange={onChange}
					           fullWidth={true}
					           focused={focused}
					           multiline={true}
					           minRows={1}
					           maxRows={3}
					           size='small'
					           sx={{backgroundColor: 'white'}}
					/>

					<IconButton type={Icons.COMMENT_ADD}
					            label='Add'
					            onClick={onAdd}
					            disabled={isAddDisabled}
					            useDelay={true}
					/>

					<IconButton type={sortIcon}
					            onClick={onSort}
					            label='Sort'
					/>
				</div>
            }

            {/* The comments associated with this entity */}
            <Searchbar keyName={`comment-search-${entityType}-${entityId}`}
                       label="Search comments" terms={searchTerms} onSearch={onSearch} showOptions={false}/>
            {query && !_comments.length && <p>No comments match your search.</p>}
            <div className="comments__comment-list">
                {_comments
                    .filter(util.existsFilter)
                    .map(comment => {
                        return <InView key={comment.id}>
                            {({inView, ref: inViewRef}) => {
                                return <div ref={inViewRef} id={`comment-card-${comment.id}`}>
                                    <CommentCard comment={comment}
                                                 onDelete={onDelete}
                                                 onEdit={onEdit}
                                                 parentEntityType={entityType}
                                                 isEditing={isEditing}
                                                 visible={inView}
                                    />
                                </div>
                            }}
                        </InView>;
                    })}
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        users: state.users,
        comments: state.comments,
        contacts: state.contacts,
        matters: state.matters,
        leads: state.leads,
        tasks: state.tasks
    };
};

export type CommentsProps = ReturnType<typeof mapStateToProps> & {
    entityId: string,
    entityType: string,
    formatRelated?: boolean,
    embedded?: boolean,
    isEditing?: boolean, // Added by ContentBlock
}

type SortKeys = 'asc' | 'desc';


export default connect(mapStateToProps)(Comments);
